import { useContext, useEffect, useState } from 'react';
import {
  Backdrop,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Fade,
  Grid,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import { BarChart, LineChart, PieChart } from '@mui/x-charts';
import { DashboardContext } from 'contexts/DashboardContext';
import { AuthContext } from 'contexts/AuthContext';
import Container from 'components/Container';
import Loader from 'components/Loader';
import Pizza from './Charts/Pizza';
import Line from './Charts/Line';
import Bar from './Charts/Bar';
import moment from 'moment';
import styles from './styles';

const App = () => {
  const theme = useTheme();
  const [type, setType] = useState('valor');
  const { user } = useContext(AuthContext);
  const { dashData, getLoading, getDashboard } = useContext(DashboardContext);

  const pieColor = {
    PENDENTE: theme?.palette?.warning?.main,
    CONCLUIDO: theme?.palette?.success?.main,
    CANCELADO: theme?.palette?.error?.main,
  };

  const pieCharts = [
    {
      title: 'Orçamentos',
      data: dashData?.orçamentos,
      show: !user?.permissoes?.block_dashboard_orcamentos,
    },
    {
      title: 'Pedidos',
      data: dashData?.pedidos,
      show: !user?.permissoes?.block_dashboard_pedidos,
    },
    {
      title: 'Faturas',
      data: dashData?.faturas,
      show: !user?.permissoes?.block_dashboard_faturas,
    },
  ];

  const pieSeries = (arr) => [
    {
      data: arr.map((d) => ({ ...d, color: pieColor[d.label] })),
      innerRadius: 40,
      outerRadius: 80,
      paddingAngle: 4,
      cornerRadius: 5,
    },
  ];

  const lineSeries = (arr) => [
    {
      showMark: false,
      curve: 'monotone',
      label: 'Faturamento (R$)',
      color: theme?.palette?.primary?.main,
      data: arr?.map((item) => item?.valor || 0),
      valueFormatter: (v) =>
        v?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
    },
  ];

  const lineXAxis = (arr) => [
    {
      scaleType: 'band',
      data: arr?.map((item) => item?.dia),
      valueFormatter: (v) =>
        `${String(v)?.padStart(2, '0')}/${moment().format('MM')}`,
    },
  ];

  const lineYAxis = [
    {
      valueFormatter: (v) =>
        v?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
    },
  ];

  const barSeries = [
    {
      dataKey: type,
      label: type === 'quantidade' ? 'Quantidade' : 'Valor (R$)',
      color: theme.palette.primary.main,
      valueFormatter: (v) =>
        type === 'quantidade'
          ? v
          : v?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
    },
  ];

  const barXAxis = [
    {
      scaleType: 'band',
      dataKey: 'nome',
      tickLabelStyle: { fontSize: 10, whiteSpace: 'normal' },
      categoryGapRatio: 0.75,
    },
  ];

  const barYAxis = [
    {
      valueFormatter: (v) =>
        type === 'quantidade'
          ? v
          : v?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
    },
  ];

  useEffect(() => {
    getDashboard();
  }, []);

  return (
    <Container>
      <Grid container spacing={2}>
        {pieCharts?.map(
          ({ title, data = [], show }, idx) =>
            show && (
              <Grid item xs={12} lg={4} key={idx}>
                <Card sx={styles.card}>
                  <CardContent>
                    <Typography variant="h6">{title}</Typography>
                    {Boolean(data?.length) ? (
                      <PieChart
                        height={250}
                        series={pieSeries(data)}
                        margin={styles?.pieMargin}
                        slotProps={styles?.pieSlot}
                      />
                    ) : (
                      <Typography textAlign="center" variant="body2">
                        Nenhum registro encontrado
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )
        )}
        {!user?.permissoes?.block_dashboard_faturamentos && (
          <Grid item xs={12}>
            <Card sx={styles?.card}>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="h6">
                    Faturamento por vendedor -{' '}
                    {moment().format('MMMM [de] YYYY')}
                  </Typography>
                  <Select
                    size="small"
                    value={type}
                    onChange={(e) => setType(e?.target?.value)}
                  >
                    <MenuItem value="quantidade">QUANTIDADE</MenuItem>
                    <MenuItem value="valor">VALOR</MenuItem>
                  </Select>
                </Box>
                {Boolean(dashData?.faturamentos?.length) ? (
                  <BarChart
                    height={300}
                    xAxis={barXAxis}
                    yAxis={barYAxis}
                    series={barSeries}
                    margin={styles?.lineMargin}
                    slotProps={styles?.lineSlot}
                    dataset={dashData?.faturamentos}
                  />
                ) : (
                  <Typography textAlign="center" variant="body2">
                    Nenhum registro encontrado
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
        {!user?.permissoes?.block_dashboard_faturamentos_dia && (
          <Grid item xs={12}>
            <Card sx={styles?.card}>
              <CardContent>
                <Typography variant="h6">
                  Faturamento diário - {moment().format('MMMM [de] YYYY')}
                </Typography>
                {Boolean(dashData?.faturamentos_dia?.length) ? (
                  <LineChart
                    height={300}
                    yAxis={lineYAxis}
                    margin={styles?.lineMargin}
                    slotProps={styles?.lineSlot}
                    xAxis={lineXAxis(dashData?.faturamentos_dia)}
                    series={lineSeries(dashData?.faturamentos_dia)}
                  />
                ) : (
                  <Typography textAlign="center" variant="body2">
                    Nenhum registro encontrado
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
      <Fade in={getLoading}>
        <Backdrop open={getLoading} sx={styles?.loader}>
          <CircularProgress color="inherit" size={48} thickness={4} />
        </Backdrop>
      </Fade>
    </Container>
  );
};

export default App;
